import { Box, Center, IconButton, Spinner, Stack, Text, useToast } from '@chakra-ui/react';
import { PageHeading } from '@duke-hq/ui/components/PageHeading';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { ArrowRightFromLine } from 'lucide-react';
import { ProviderForm } from './ProviderForm';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { getZodFieldErrorMessages } from '../lib/zodErrorMessages';
import { useParams } from 'react-router-dom';
import TableViewLayout from '@duke-hq/ui/components/TableViewLayout/TableViewLayout';
import { useSubSidebar } from '@duke-hq/ui/components/SubSidebar/useSubSidebar';
import { withPermission } from '../withPermission';

function ProviderHeader({
  isExpanded,
  toggleExpandSubSidebar,
}: {
  isExpanded: boolean;
  toggleExpandSubSidebar: () => void;
}) {
  return (
    <>
      {!isExpanded && (
        <IconButton
          aria-label={t`expand secondary sidebar`}
          icon={<ArrowRightFromLine />}
          variant="tertiary"
          onClick={toggleExpandSubSidebar}
          size="md"
        />
      )}
      <PageHeading>
        <Trans>Settings</Trans>
      </PageHeading>
    </>
  );
}

export const ProviderSettings = withPermission('canViewProviders', () => {
  const { providerId } = useParams();
  const toast = useToast();
  const { isExpanded, toggleExpandSubSidebar } = useSubSidebar();
  const { data, isFetching, refetch } = trpcAdmin.providers.byId.useQuery(
    parseInt(providerId ?? '0', 10),
  );
  const updateProvider = trpcAdmin.providers.update.useMutation({
    onSuccess: () => {
      toast({
        title: t`Provider updated.`,
        description: t`Provider updated successfully. It might take up to 60 seconds for the changes to take effect.`,
        status: 'success',
        duration: 7000,
        isClosable: true,
      });
      void refetch();
    },
    onError: (error) => {
      const zodErrors = getZodFieldErrorMessages(error);

      toast({
        title: t`Error updating provider`,
        description: zodErrors.length > 0 ? zodErrors.join(' ') : error.message,
        status: 'error',
        duration: 7000,
        isClosable: true,
      });
    },
  });

  const handleSubmit = updateProvider.mutateAsync;

  if (!providerId) {
    return (
      <Center>
        <Text>
          <Trans>Provider ID is required</Trans>
        </Text>
      </Center>
    );
  }

  if (isFetching) {
    return (
      <Center>
        <Spinner />
      </Center>
    );
  }

  if (!data) {
    return (
      <Center>
        <Trans>No providers found 🤔</Trans>
      </Center>
    );
  }

  return (
    <TableViewLayout
      pageHeader={
        <ProviderHeader isExpanded={isExpanded} toggleExpandSubSidebar={toggleExpandSubSidebar} />
      }
      table={
        <Box
          bg="bg.surface"
          boxShadow={{ base: 'none', md: 'sm' }}
          borderRadius={{ base: 'none', md: 'lg' }}
        >
          <Stack spacing="5">
            <Box overflowX="auto" p={10}>
              <ProviderForm
                provider={data}
                providerId={data.id}
                disabled={isFetching || updateProvider.isPending}
                onSubmit={handleSubmit}
              />
            </Box>
          </Stack>
        </Box>
      }
    />
  );
});
